import * as React from 'react';
import { Dispatch } from 'react-redux';
import { Translate } from 'react-redux-i18n';

import { Api } from '../../../typings/api';
import Utils from '../../../utils';
import UrlUtils from '../../../utils/url';
import UserAgentUtils from '../../../utils/userAgent';
import { StoreState } from '../../../reducers';
import { GalleryInfosState } from '../../../containers/Gallery/reducer';
import { DownloadButtonVariants } from './styles';
import { requestWebDownloadItem, requestMobileAppDownload, downloadRecursively } from '../../../containers/DownloadManager/actions';
import Tooltip from '../../../components/Tooltip';
import { ActionButtonStyle, ActionButton } from '../../../utils/driveItem';
import Loader, { LoaderStep, LoaderType, LoaderStepName } from '../../../components/Loader';
import { DownloadMode } from '../../../containers/DownloadManager/reducer';
import StatsUtils from '../../../utils/statistics';
import { getMenuButtonIcon } from '../../../utils/menuManager';

const RESET_DELAY = 3000;

export interface DriveItemDownloadButtonProps {
	driveItems: Api.DriveItem[];
	dispatch: Dispatch<StoreState>;
	style: ActionButtonStyle;
	action?: ActionButton;
	galleryInfos: GalleryInfosState;
	isDownloading: boolean;
	isDeletingAll: boolean;
	loaderStep: LoaderStep;
	iconSrc: string;
	icon?: string;
	label?: string;
}

interface DriveItemDownloadButtonState {
	clicked: boolean;
}

class DriveItemDownloadButton extends React.Component<DriveItemDownloadButtonProps, DriveItemDownloadButtonState> {

	constructor(props: DriveItemDownloadButtonProps) {
		super(props);
		this.state = { clicked: false };
	}

	handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
		const { driveItems, dispatch, galleryInfos, action } = this.props;
		const { isMobileApp } = UserAgentUtils;

		if (action !== ActionButton.Update) {
			StatsUtils.trackDownload(driveItems);
		}
		const driveItemsIds = driveItems.map(driveItem => driveItem.id);

		if (isMobileApp) {
			if (driveItems.length === 1) {
				dispatch(requestMobileAppDownload(
					driveItems[0],
					DownloadMode.Requested,
					undefined,
					action === ActionButton.Update,
				));
				return;
			}

			driveItemsIds.forEach(itemId => {
				dispatch(downloadRecursively(itemId, DownloadMode.Requested));
			});
			return;
		}

		this.setState({ clicked: true });
		setTimeout(() => this.setState({ clicked: false }), RESET_DELAY);

		if (driveItems.length === 1) {
			dispatch(requestWebDownloadItem(driveItems[0]));
			return;
		}

		window.location.href = UrlUtils.getResourcesGroupUrl(galleryInfos.config, driveItemsIds);
	}

	renderButtonContent = () => {
		const { style, action, isDownloading, loaderStep, iconSrc } = this.props;
		const isPhone = UserAgentUtils.isPhone();

		if (style === ActionButtonStyle.Icon) {
			return getMenuButtonIcon(iconSrc, 'iconset_uprez_download');
		}

		if (isDownloading) {
			return (
				<Loader
					loaderStep={loaderStep || { step: LoaderStepName.Downloading, progress: {} }}
					type={LoaderType.Button}
				/>
			);
		}

		if (action === ActionButton.Update) {
			return isPhone ? Utils.getSvgIcon('iconset_update') : <Translate value="messages.update" />;
		}

		return <Translate value="messages.download" />;
	}

	render() {
		const {
			action,
			style,
			isDownloading,
			isDeletingAll,
			label,
			galleryInfos: { template: { commonStyles: { primary: { strongColor } } } },
		} = this.props;
		const { clicked } = this.state;
		const isFlickering = isDownloading || clicked;
		const tooltipId = Utils.generateId();
		const DownloadButton = DownloadButtonVariants[style];

		return !isDeletingAll ?
			(
				<DownloadButton
					onClick={this.handleClick}
					isFlickering={isFlickering}
					color={strongColor}
					action={action}
					data-tip={style === ActionButtonStyle.Icon}
					data-for={tooltipId}
				>
					{this.renderButtonContent()}
					{(UserAgentUtils.isDesktop() && style === ActionButtonStyle.Icon) &&
						<Tooltip id={tooltipId} text={label || 'messages.download'} />
					}
				</DownloadButton>
			) :
			null;
	}
}

export default DriveItemDownloadButton;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/DriveItem/DownloadButton/index.tsx